import type { Bill } from "../api-modules/types.js";

/**
 * Buckets a flat list of bills into the four time-based sections the
 * `/bills` page renders, plus a separate `archived` group:
 *
 *   - `overdue`  — non-archived, due_date < today
 *   - `due7d`    — non-archived, today ≤ due_date ≤ today + 7 days
 *   - `due30d`   — non-archived, today + 7 < due_date ≤ today + 30
 *   - `later`    — non-archived, due_date > today + 30
 *   - `archived` — is_archived === true (regardless of due date)
 *
 * `today` accepts an ISO date string (YYYY-MM-DD) so tests can pin time.
 * Comparison is purely lexicographic on YYYY-MM-DD strings — no Date
 * arithmetic, no timezone subtleties.
 *
 * Within each non-archived bucket, bills are sorted by ascending
 * due_date. Within `archived`, bills are sorted by descending
 * `last_paid_date` (most recently paid first), falling back to
 * `updated_at` when `last_paid_date` is missing.
 */

export interface BillsBuckets {
  overdue: Bill[];
  due7d: Bill[];
  due30d: Bill[];
  later: Bill[];
  archived: Bill[];
}

export function bucketBills(bills: Bill[], today: string): BillsBuckets {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(today)) {
    throw new Error(`bucketBills: invalid today "${today}", expected YYYY-MM-DD`);
  }
  const todayPlus7 = isoDateAddDays(today, 7);
  const todayPlus30 = isoDateAddDays(today, 30);

  const out: BillsBuckets = {
    overdue: [],
    due7d: [],
    due30d: [],
    later: [],
    archived: [],
  };

  for (const b of bills) {
    if (b.is_archived === true) {
      out.archived.push(b);
      continue;
    }
    if (b.due_date < today) {
      out.overdue.push(b);
    } else if (b.due_date <= todayPlus7) {
      out.due7d.push(b);
    } else if (b.due_date <= todayPlus30) {
      out.due30d.push(b);
    } else {
      out.later.push(b);
    }
  }

  for (const k of ["overdue", "due7d", "due30d", "later"] as const) {
    out[k].sort((a, b) => a.due_date.localeCompare(b.due_date));
  }
  out.archived.sort((a, b) => archivedSortKey(b).localeCompare(archivedSortKey(a)));
  return out;
}

function archivedSortKey(b: Bill): string {
  return b.last_paid_date ?? b.updated_at;
}

/** Add `days` to an ISO YYYY-MM-DD string (UTC). */
export function isoDateAddDays(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  const t = Date.UTC(y, m - 1, d) + days * 24 * 60 * 60 * 1000;
  const out = new Date(t);
  return `${out.getUTCFullYear().toString().padStart(4, "0")}-${pad2(
    out.getUTCMonth() + 1,
  )}-${pad2(out.getUTCDate())}`;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
